import { Alert, AlertTitle, Box, Button, Checkbox, Container, FormControlLabel, Grid, MenuItem, TextField, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { APIS, STUDENT_APIS } from "../../../modules/common/api/apis";
import { useNotify } from "../../../context/NotificationContext";
import { getPersonByMobile, Person } from "../../../modules/common/shared/apis/PersonAPI";
import { getAxiosHeaders, post } from "../../../modules/common/shared/AxiosAPI";
import { getNotes, initializeStudent, removeNullAndUndefinedDeep } from "../../../modules/common/shared/StudentAPI";
import { ActionType, GuardianType, Student } from "../../../modules/common/shared/types";
import {
  getStaticSections,
  getNationalities,
  getPreviousYears,
  getReligions,
  getStates,
  getStaticAreas,
  getStaticBloodGroups,
  getStaticGrades,
  getStaticLanguages,
  getStudentRelationsExceptParents,
  getStudentRelations,
} from "../../../modules/common/shared/Util";
import { isValidMobileNo } from "../../../modules/common/shared/util/FormValidationUtil";
import { MobileNumberField } from "../../../components/common/form/MobileNumberField";

// The editable identity fields for Father/Mother/Guardian (Emergency only ever collects
// a name, so it doesn't need this). Grouped into one object per person because a mobile
// lookup match populates and locks all of them together, not field by field.
type EditablePersonFields = {
  name: string;
  dob: Dayjs | null;
  qualification: string;
  occupation: string;
  monthlyIncome: string;
  email: string;
};

const toEditableFields = (person?: Person): EditablePersonFields => ({
  name: person?.name ?? "",
  dob: person?.dob ? dayjs(person.dob) : null,
  qualification: person?.qualification ?? "",
  occupation: person?.occupation ?? "",
  monthlyIncome: person?.monthlyIncome != null ? String(person.monthlyIncome) : "",
  email: person?.email ?? "",
});

// Mobile number now comes first in each section — entering a full 10-digit number looks
// the person up. A match populates and locks the rest of that section's fields (existing
// person, edited elsewhere); anything else leaves them open for a fresh entry.
const lookupPersonByMobile = (
  value: string | null,
  setFields: Dispatch<SetStateAction<EditablePersonFields>>,
  setLocked: Dispatch<SetStateAction<boolean>>
) => {
  if (!value || value.length !== 10) {
    setLocked(false);
    return;
  }
  getPersonByMobile(Number.parseInt(value))
    .then((res) => {
      if (res.status === 200) {
        setFields(toEditableFields(res.data));
        setLocked(true);
      }
    })
    .catch((err) => {
      if (err.response?.status === 404) {
        setFields(toEditableFields(undefined));
        setLocked(false);
      }
    });
};

// guardian flag and relation are UI-only; the backend uses the *ContactRelation fields
const stripUiFields = (person?: Person) => {
  if (!person) return person;
  const { guardian, relation, ...rest } = person as Person & { guardian?: boolean; relation?: string };
  return rest as Person;
};

// Admin create (/students) payload: guardian/emergency are sent only when they are not
// the father or mother, and null/undefined values are dropped.
const toAdminPayload = (formStd: Student) => {
  const isParentRelation = (relation?: string) => relation === GuardianType.FATHER || relation === GuardianType.MOTHER;
  const payload: Partial<Student> = {
    ...formStd,
    father: stripUiFields(formStd.father),
    mother: stripUiFields(formStd.mother),
    guardian: stripUiFields(formStd.guardian),
    emergency: stripUiFields(formStd.emergency),
  };
  if (isParentRelation(formStd.guardianContactRelation)) delete payload.guardian;
  if (isParentRelation(formStd.emergencyContactRelation)) delete payload.emergency;
  return removeNullAndUndefinedDeep(payload);
};

type StudentAdmissionFormProps = {
  // PARENT: public admission from a verified enquiry — student name/grade/DOB are locked,
  //         posts to the public admission API, then calls onParentSubmitted.
  // ADMIN:  admin creating a student — every field editable, plus Section; posts to
  //         /students and, on success, shows a toast and calls onAdminCreated (to reset).
  mode: "PARENT" | "ADMIN";
  inputStudent: Student;
  onAdminCreated?: () => void;
  // PARENT success: the page swaps to its confirmation card (see AdmissionApply).
  onParentSubmitted?: (submitted: SubmittedAdmission) => void;
};

export type SubmittedAdmission = { name: string; gradeName: string };

// Create-only student form, shared by the public admission page and admin "Add student".
export const StudentAdmissionForm = ({ mode, inputStudent, onAdminCreated, onParentSubmitted }: StudentAdmissionFormProps) => {
  const isParent = mode === "PARENT";

  const notify = useNotify();
  const [rowClassName, setRowClassName] = useState<string>(() => (window.innerWidth < 720 ? "justify-content-center" : ""));
  const [areas] = useState<string[]>(() => getStaticAreas());
  const [isStdSplMedicalAbled, setIsStdSplMedicalAbled] = useState<boolean>(false);
  const [isStdSplLearningAbled, setIsStdSplLearningAbled] = useState<boolean>(false);
  const [permAddIsSameAsPresent, setPermAddIsSameAsPresent] = useState<boolean>(true);
  // MobileNumberField is a controlled component (see components/common/form), so these mobile
  // numbers — previously uncontrolled fields auto-filled imperatively via ref.current.value —
  // are tracked as state instead, same treatment as the DOB pickers above.
  const [fatherMobile, setFatherMobile] = useState<string | null>(inputStudent.father?.mobileNo ? String(inputStudent.father.mobileNo) : null);
  const [motherMobile, setMotherMobile] = useState<string | null>(inputStudent.mother?.mobileNo ? String(inputStudent.mother.mobileNo) : null);
  const [guardianMobile, setGuardianMobile] = useState<string | null>(
    inputStudent.guardian?.mobileNo ? String(inputStudent.guardian.mobileNo) : null
  );
  const [emergencyPhone, setEmergencyPhone] = useState<string | null>(
    inputStudent.emergency?.mobileNo ? String(inputStudent.emergency.mobileNo) : null
  );
  // Same reasoning as the mobile fields above: auto-filled via lookupEmergencyPerson below,
  // so it needs to be controlled state rather than a ref for MUI's label to shrink correctly
  // once the value is populated programmatically.
  const [emergencyName, setEmergencyName] = useState<string>(inputStudent.emergency?.name ?? "");
  // Set once a mobile lookup finds an existing person; locks the rest of that section's
  // fields (disabled) so an existing record can't be silently edited from this form.
  const [emergencyLocked, setEmergencyLocked] = useState<boolean>(false);
  // inputStudent arrives synchronously from AdmissionApply (guaranteed by its guard above),
  // so it's read once here as the initial state rather than copied in via an effect.
  const [student, setStudent] = useState<Student>(() => {
    inputStudent.previousSchools = [];
    return { ...inputStudent };
  });
  const [guardian, setGuardian] = useState<string>(GuardianType.FATHER);
  // Bootstrap's native <select> allowed setting the visible value imperatively via
  // `ref.current.value = ...`. MUI's Select is not driven by the DOM, so religion and
  // guardian (above) are tracked as controlled state instead of an uncontrolled ref.
  const [religion, setReligion] = useState<string>(inputStudent.religion ?? "");
  // The DOB picker for the student themselves is still its own controlled value — only
  // Father/Mother/Guardian's DOB moved into their grouped EditablePersonFields below,
  // since it now populates and locks alongside their other fields on a mobile match.
  const [dobValue, setDobValue] = useState<Dayjs | null>(inputStudent.dob ? dayjs(inputStudent.dob) : null);
  // Name/DOB/qualification/occupation/income/email for Father, Mother, and Guardian
  // (when "Other") — grouped per person so a mobile-lookup match can populate and lock
  // all of them together. See lookupPersonByMobile above.
  const [fatherFields, setFatherFields] = useState<EditablePersonFields>(() => toEditableFields(inputStudent.father));
  const [fatherLocked, setFatherLocked] = useState<boolean>(false);
  const [motherFields, setMotherFields] = useState<EditablePersonFields>(() => toEditableFields(inputStudent.mother));
  const [motherLocked, setMotherLocked] = useState<boolean>(false);
  const [guardianFields, setGuardianFields] = useState<EditablePersonFields>(() => toEditableFields(inputStudent.guardian));
  const [guardianLocked, setGuardianLocked] = useState<boolean>(false);
  const [pSchl1Status, setPSchl1Status] = useState<number>(0);
  const [pSchl2Status, setPSchl2Status] = useState<number>(0);
  const [isAdmissionFormSubmitting, setIsAdmissionFormSubmitting] = useState<boolean>(false);

  //Refs
  const stdSplMedicalAbledRef = useRef<HTMLInputElement>(null);
  const stdSplLearningAbledRef = useRef<HTMLInputElement>(null);

  //choose the screen size
  const handleResize = () => {
    if (window.innerWidth < 720) {
      setRowClassName("justify-content-center");
    } else {
    }
  };

  // create an event listener
  useEffect(() => {
    window.addEventListener("resize", handleResize);
  });

  const submit = (form: any) => {
    form.preventDefault();
    setIsAdmissionFormSubmitting(true);

    const formData = new FormData(form.currentTarget);
    // DatePicker fields aren't native inputs, so their ISO value is injected here instead
    // of being collected automatically from the DOM (matches the empty-string-when-unset
    // behavior the native <input type="date"> fields used to produce).
    formData.set("dob", dobValue && dobValue.isValid() ? dobValue.format("DD-MMM-YYYY") : undefined);
    if (fatherFields.dob && fatherFields.dob.isValid())
      formData.set("f-dob", fatherFields.dob.format("DD-MMM-YYYY"));
    formData.set("g-dob", guardianFields.dob && guardianFields.dob.isValid() ? guardianFields.dob.format("DD-MMM-YYYY") : undefined);
    // `student` (not inputStudent) so an admin-typed name is picked up; for a parent the
    // name field is locked, so both are the same.
    const formStd: Student = initializeStudent(formData, ActionType.CREATE, mode, permAddIsSameAsPresent, student);

    // Name/qualification/occupation/income/email are controlled state now, and locked
    // (mobile-matched) fields are rendered `disabled` — browsers exclude disabled fields
    // from native form submission, so FormData can't be trusted for these; applied here
    // directly instead, whether the section is locked or freshly typed.
    formStd.father = {
      ...formStd.father,
      name: fatherFields.name,
      qualification: fatherFields.qualification,
      occupation: fatherFields.occupation,
      monthlyIncome: fatherFields.monthlyIncome ? Number(fatherFields.monthlyIncome) : undefined,
      email: fatherFields.email,
    };
    formStd.mother = {
      ...formStd.mother,
      name: motherFields.name,
      qualification: motherFields.qualification,
      occupation: motherFields.occupation,
      monthlyIncome: motherFields.monthlyIncome ? Number(motherFields.monthlyIncome) : undefined,
      email: motherFields.email,
    };
    if (guardian === GuardianType.FATHER) {
      formStd.guardian = formStd.father;
    } else if (guardian === GuardianType.MOTHER) {
      formStd.guardian = formStd.mother;
    } else {
      formStd.guardian = {
        ...formStd.guardian,
        name: guardianFields.name,
        qualification: guardianFields.qualification,
        occupation: guardianFields.occupation,
        monthlyIncome: guardianFields.monthlyIncome ? Number(guardianFields.monthlyIncome) : undefined,
        email: guardianFields.email,
      };
    }

    formStd.gradeName = student.gradeName;
    formStd.sectionName = isParent ? "A" : student.sectionName;
    if (formStd.gradeName === null || formStd.gradeName === undefined) {
      alert("Grade name not defined. ");
      setIsAdmissionFormSubmitting(false);
      return;
    }
    //Validate guardian phone
    if (!isValidMobileNo(formStd.guardian.mobileNo.toString())) {
      alert("Invalid guardian mobile no.");
      setIsAdmissionFormSubmitting(false);
      return;
    }
    if (guardian === GuardianType.FATHER)
      formStd.guardianContactRelation = GuardianType.FATHER;
    else if (guardian === GuardianType.MOTHER)
      formStd.guardianContactRelation = GuardianType.MOTHER;
    else
      formStd.guardianContactRelation = formData.get("g-relation")?.toString();

    const cERelation = formData.get("emg-relation")?.toString();
    formStd.emergencyContactRelation = cERelation;

    const headers = getAxiosHeaders();
    if (!isParent) headers.set("requestFrom", "ADMIN");
    const url = isParent ? APIS.PUBLIC_ADMISSION_STUDENT : STUDENT_APIS.STUDENTS;

    post(url, headers, isParent ? formStd : toAdminPayload(formStd))
      .then((res) => {
        if (res.status === 201) {
          if (isParent) {
            onParentSubmitted?.({ name: student.name, gradeName: student.gradeName });
          } else {
            setIsAdmissionFormSubmitting(false);
            notify("Student created successfully.", "success");
            onAdminCreated?.();
          }
          // the form is long; bring the confirmation card (or the fresh form) into view
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
        console.log(res);
      })
      .catch((err) => {
        setIsAdmissionFormSubmitting(false);
        alert(err.response.data);
      });
  };

  const lookupEmergencyPerson = (value: string | null) => {
    if (!value || value.length !== 10) {
      setEmergencyLocked(false);
      return;
    }
    getPersonByMobile(Number.parseInt(value))
      .then((res) => {
        if (res.status === 200) {
          setEmergencyName(res.data.name);
          setEmergencyLocked(true);
        }
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          setEmergencyName("");
          setEmergencyLocked(false);
        }
      });
  };
  const rowJustify = rowClassName ? "center" : "flex-start";

  // Section heading: accent bar, bold title, then a hairline that runs to the edge.
  const sectionHeadingSx = {
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    fontWeight: 700,
    fontSize: { xs: "0.95rem", md: "1.05rem" },
    letterSpacing: "-0.01em",
    color: "#0f172a",
    mt: { xs: 1.5, md: 2 },
    mb: 0.5,
    "&::before": {
      content: '""',
      width: 4,
      height: 18,
      borderRadius: 2,
      bgcolor: "primary.main",
      flexShrink: 0,
    },
    "&::after": {
      content: '""',
      flex: 1,
      height: "1px",
      bgcolor: "#e5e7eb",
      ml: 0.5,
    },
  };

  return (
    <Container maxWidth="xl" sx={{ pt: { xs: 2, md: 3 }, pb: 4 }}>
      <Grid container sx={{ pb: 1 }}>
        <Grid size={{ xs: 12, lg: 12 }}>
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon />}
            sx={{ mb: 1, borderRadius: 2, border: "1px solid", borderColor: "info.light", alignItems: "flex-start" }}
          >
            <AlertTitle sx={{ fontWeight: 600, mb: 0.25 }}>Use official document details</AlertTitle>
            <Typography sx={{ fontSize: { xs: 13, sm: 14 }, color: "text.secondary" }}>
              Ensure that the details you provide match those on your official government-issued documents.
            </Typography>
          </Alert>
        </Grid>
      </Grid>
      <Box
        component="form"
        onSubmit={submit}
        sx={{
          bgcolor: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 3,
          boxShadow: "0 12px 40px rgba(16, 24, 40, 0.06)",
          px: { xs: 2, sm: 3, md: 4 },
          pt: { xs: 2.5, md: 3.5 },
          pb: 1,
        }}
      >
        <Grid container spacing={2} sx={{ justifyContent: rowJustify, mb: 2 }}>
          <Grid size={{ xs: 10, lg: 4 }}>
            <TextField
              size="small"
              name="sName"
              type="text"
              label="Name of the Student *"
              placeholder="Name of the Student *"
              required
              disabled={isParent}
              InputLabelProps={{ required: false }}
              fullWidth
              value={student.name}
              InputProps={{ readOnly: isParent }}
              onChange={(e) => setStudent({ ...student, name: e.target.value.toUpperCase() })}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              select
              label="Grade"
              name="std-grade"
              disabled={isParent}
              required
              InputLabelProps={{ required: false }}
              fullWidth
              value={student.gradeName ?? ""}
              onChange={(e) => setStudent({ ...student, gradeName: e.target.value })}
            >
              {getStaticGrades()?.map((grade: string) => (
                <MenuItem value={grade} key={"std" + grade}>
                  {grade}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {!isParent ? (
            <Grid size={{ xs: 10, lg: 2 }}>
              <TextField
                size="small"
                select
                label="Section"
                name="std-section"
                required
                InputLabelProps={{ required: false }}
                fullWidth
                value={student.sectionName ?? ""}
                onChange={(e) => setStudent({ ...student, sectionName: e.target.value })}
              >
                {getStaticSections()?.map((section: string) => (
                  <MenuItem value={section} key={"std" + section}>
                    {section}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          ) : (
            ""
          )}
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              select
              label="Gender"
              name="gender"
              fullWidth
              value={student.gender ?? ""}
              onChange={(e) => setStudent({ ...student, gender: e.target.value })}
            >
              {[
                { k: "Male", v: "M" },
                { k: "Female", v: "F" },
              ].map((gender: any) => (
                <MenuItem value={gender.v} key={gender.v}>
                  {gender.k}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <DatePicker
              label="Date of Birth *"
              format="DD-MMM-YYYY"
              value={dobValue}
              onChange={(newValue) => setDobValue(newValue)}
              maxDate={dayjs()}
              readOnly={isParent}
              disabled={isParent}
              slotProps={{
                textField: {
                  size: "small",
                  required: true,
                  InputLabelProps: { required: false },
                  fullWidth: true,
                  // helperText: "Locked after verification — contact the school office to change this.",
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              name="placeOfBirth"
              type="text"
              label="City of birth *"
              placeholder="City of birth *"
              defaultValue={student.placeOfBirth}
              required
              InputLabelProps={{ required: false }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              select
              label="Religion"
              name="religion"
              fullWidth
              value={religion ?? ""}
              onChange={(e) => setReligion(e.target.value)}
            >
              {getReligions().map((religionOpt: string) => (
                <MenuItem value={religionOpt} key={"reli" + religionOpt}>
                  {religionOpt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              select
              label="Nationality"
              name="nationality"
              fullWidth
              value={student.nationality ?? ""}
              onChange={(e) => setStudent({ ...student, nationality: e.target.value })}
            >
              {getNationalities().map((nationality: string) => (
                <MenuItem value={nationality} key={"nationality" + nationality}>
                  {nationality}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              select
              label="Mother Tongue"
              name="mTongue"
              fullWidth
              value={student.motherTongue ?? ""}
              onChange={(e) => setStudent({ ...student, motherTongue: e.target.value })}
            >
              {getStaticLanguages().map((mtng: string) => (
                <MenuItem value={mtng} key={"mTongue" + mtng}>
                  {mtng}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              select
              label="Blood Group"
              name="bGroup"
              fullWidth
              value={student.bloodGroup ?? ""}
              onChange={(e) => setStudent({ ...student, bloodGroup: e.target.value })}
            >
              {getStaticBloodGroups().map((mtng: string) => (
                <MenuItem value={mtng} key={"mTongue" + mtng}>
                  {mtng}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ justifyContent: rowJustify, mb: 2 }}>
          <Grid size={{ xs: 12, lg: 12 }}>
            <Typography variant="subtitle1" sx={sectionHeadingSx}>
              Present Address
            </Typography>
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              name="pr-a-h"
              type="text"
              label="House No *"
              required
              InputLabelProps={{ required: false }}
              fullWidth
              defaultValue={student.present?.houseNo}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              name="pr-a-s"
              type="text"
              label="Street/Road *"
              placeholder="Street/Road *"
              required
              InputLabelProps={{ required: false }}
              fullWidth
              defaultValue={student.present?.streetRoad}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              select
              label="Area/ locality"
              name="pr-a-a"
              fullWidth
              value={student.present?.areaLocality ?? ""}
              onChange={(e) => setStudent({ ...student, present: { ...student.present, areaLocality: e.target.value } })}
            >
              {areas?.map((area: string) => (
                <MenuItem value={area} key={area}>
                  {area}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              name="pr-a-l"
              type="text"
              label="Landmark"
              placeholder="Landmark"
              fullWidth
              defaultValue={student.present?.landmark}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              name="pr-a-c"
              type="text"
              label="City *"
              placeholder="City"
              required
              InputLabelProps={{ required: false }}
              fullWidth
              defaultValue={student.present?.city}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              select
              label="State *"
              name="pr-a-st"
              required
              InputLabelProps={{ required: false }}
              fullWidth
              value={student.present?.state ?? ""}
              onChange={(e) => setStudent({ ...student, present: { ...student.present, state: e.target.value } })}
            >
              {getStates()?.map((state: string) => (
                <MenuItem value={state} key={"pr-a" + state}>
                  {state}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              name="pr-a-pin"
              type="text"
              label="Pincode *"
              placeholder="Pincode *"
              required
              InputLabelProps={{ required: false }}
              slotProps={{ htmlInput: { maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" } }}
              fullWidth
              value={student.present?.pincode ?? ""}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                setStudent({ ...student, present: { ...student.present, pincode: value ? Number(value) : 0 } });
              }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ justifyContent: rowJustify, mb: 2, alignItems: "center" }}>
          <Grid size={{ xs: 12, lg: 12 }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="permanent-address-is-same-as-present"
                  checked={permAddIsSameAsPresent}
                  onChange={(e) => setPermAddIsSameAsPresent(e.target.checked)}
                />
              }
              label="Use present address as permanent address"
            />
          </Grid>
          {!permAddIsSameAsPresent ? (
            <>
              <Grid size={{ xs: 12, lg: 12 }}>
                <Typography variant="subtitle1" sx={sectionHeadingSx}>
                  Permanent Address
                </Typography>
              </Grid>
              <Grid size={{ xs: 10, lg: 2 }}>
                <TextField
                  size="small"
                  name="prahno"
                  type="text"
                  label="House No *"
                  placeholder="House No"
                  required
                  InputLabelProps={{ required: false }}
                  fullWidth
                  value={student.permanent?.houseNo ?? ""}
                  onChange={(e) => setStudent({ ...student, permanent: { ...student.permanent, houseNo: e.target.value } })}
                />
              </Grid>
              <Grid size={{ xs: 10, lg: 2 }}>
                <TextField
                  size="small"
                  name="pras"
                  type="text"
                  label="Street/Road *"
                  placeholder="Street/Road"
                  required
                  InputLabelProps={{ required: false }}
                  fullWidth
                  value={student.permanent?.streetRoad ?? ""}
                  onChange={(e) => setStudent({ ...student, permanent: { ...student.permanent, streetRoad: e.target.value } })}
                />
              </Grid>
              <Grid size={{ xs: 10, lg: 2 }}>
                <TextField
                  size="small"
                  select
                  label="Area/ locality"
                  name="praa"
                  fullWidth
                  value={student.permanent?.areaLocality ?? ""}
                  onChange={(e) => setStudent({ ...student, permanent: { ...student.permanent, areaLocality: e.target.value } })}
                >
                  {areas?.map((area: string) => (
                    <MenuItem value={area} key={area}>
                      {area}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 10, lg: 2 }}>
                <TextField
                  size="small"
                  name="pral"
                  type="text"
                  label="Landmark "
                  placeholder="Landmark "
                  fullWidth
                  value={student.permanent?.landmark ?? ""}
                  onChange={(e) => setStudent({ ...student, permanent: { ...student.permanent, landmark: e.target.value } })}
                />
              </Grid>
              <Grid size={{ xs: 10, lg: 2 }}>
                <TextField
                  size="small"
                  name="prac"
                  type="text"
                  label="City *"
                  placeholder="City"
                  required
                  InputLabelProps={{ required: false }}
                  fullWidth
                  value={student.permanent?.city ?? ""}
                  onChange={(e) => setStudent({ ...student, permanent: { ...student.permanent, city: e.target.value } })}
                />
              </Grid>
              <Grid size={{ xs: 10, lg: 2 }}>
                <TextField
                  size="small"
                  select
                  label="State *"
                  name="prast"
                  required
                  InputLabelProps={{ required: false }}
                  fullWidth
                  value={student.permanent?.state ?? ""}
                  onChange={(e) => setStudent({ ...student, permanent: { ...student.permanent, state: e.target.value } })}
                >
                  {getStates()?.map((state: string) => (
                    <MenuItem value={state} key={"prast" + state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 10, lg: 2 }}>
                <TextField
                  size="small"
                  name="prapin"
                  type="text"
                  label="Pincode *"
                  placeholder="Pincode *"
                  required
                  slotProps={{
                    htmlInput:
                    {
                      maxLength: 6,
                      inputMode: "numeric",
                      pattern: "[0-9]*"
                    }
                  }}
                  fullWidth
                  value={student.permanent?.pincode ?? ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setStudent({
                      ...student,
                      permanent: {
                        ...student.permanent,
                        pincode: value ? Number(value) : 0,
                      }
                    });
                  }
                  }
                />
              </Grid>
            </>
          ) : (
            ""
          )}
        </Grid>
        {student.gradeName !== "PRE-KG" ? (
          <>
            <Grid container spacing={2} sx={{ justifyContent: rowJustify, mb: 2 }}>
              <Grid size={{ xs: 12, lg: 12 }}>
                <Typography variant="subtitle1" sx={sectionHeadingSx}>
                  Previous School 1
                </Typography>
              </Grid>
              <Grid size={{ xs: 10, lg: 2 }}>
                <TextField
                  size="small"
                  name="psch1name"
                  label="Name Of The School "
                  placeholder="Name Of The School *"
                  type="text"
                  fullWidth
                  defaultValue={student.previousSchools?.length > 0 ? student.previousSchools[0]?.name : undefined}
                  inputProps={{ maxLength: 20 }}
                  onChange={(e) => setPSchl1Status(e.target.value.length)}
                />
              </Grid>
              <Grid size={{ xs: 10, lg: 2 }}>
                <TextField
                  size="small"
                  select
                  label="From *"
                  name="psch1fg"
                  disabled={pSchl1Status < 1}
                  fullWidth
                  onChange={(e) =>
                    setStudent({
                      ...student,
                      previousSchools: { ...student.previousSchools, [0]: { ...student.previousSchools[0], fromGrade: e.target.value } },
                    })
                  }
                >
                  {getStaticGrades()?.map((grade: string) => (
                    <MenuItem value={grade} key={grade}>
                      {grade}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 10, lg: 2 }}>
                <TextField
                  size="small"
                  select
                  label="To *"
                  name="psch1tg"
                  disabled={pSchl1Status < 1}
                  fullWidth
                >
                  {getStaticGrades()?.map((grade: string) => (
                    <MenuItem value={grade} key={grade}>
                      {grade}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 10, lg: 2 }}>
                <TextField
                  size="small"
                  select
                  label="From Year *"
                  name="psch1fy"
                  disabled={pSchl1Status < 1}
                  fullWidth
                >
                  {getPreviousYears(15)?.map((year: number) => (
                    <MenuItem value={year} key={"psch1fy" + year}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 10, lg: 2 }}>
                <TextField
                  size="small"
                  select
                  label="To Year *"
                  name="psch1ty"
                  disabled={pSchl1Status < 1}
                  fullWidth
                >
                  {getPreviousYears(15)?.map((year: number) => (
                    <MenuItem value={year} key={"psch1ty" + year}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {pSchl1Status >= 1 && (
              <Grid container spacing={2} sx={{ justifyContent: rowJustify, mb: 2 }}>
                <Grid size={{ xs: 12, lg: 12 }}>
                  <Typography variant="subtitle1" sx={sectionHeadingSx}>
                    Previous School 2
                  </Typography>
                </Grid>
                <Grid size={{ xs: 10, lg: 2 }}>
                  <TextField
                    size="small"
                    name="psch2name"
                    onChange={(e) => setPSchl2Status(e.target.value.length)}
                    inputProps={{ maxLength: 20 }}
                    label="Name Of The School"
                    placeholder="Name Of The School"
                    type="text"
                    fullWidth
                    defaultValue={student.previousSchools?.length > 1 ? student.previousSchools[1]?.name : undefined}
                  />
                </Grid>
                <Grid size={{ xs: 10, lg: 2 }}>
                  <TextField
                    size="small"
                    select
                    label="From"
                    disabled={pSchl2Status < 1}
                    name="psch2fg"
                    required
                    fullWidth
                    defaultValue={student.previousSchools?.length > 1 ? student.previousSchools[1]?.fromGrade : ""}
                  >
                    {getStaticGrades()?.map((grade: string) => (
                      <MenuItem value={grade} key={grade}>
                        {grade}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 10, lg: 2 }}>
                  <TextField
                    size="small"
                    select
                    label="To"
                    required
                    disabled={pSchl2Status < 1}
                    name="psch2tg"
                    fullWidth
                    defaultValue={student.previousSchools?.length > 1 ? student.previousSchools[1]?.toGrade : ""}
                  >
                    {getStaticGrades()?.map((grade: string) => (
                      <MenuItem value={grade} key={grade}>
                        {grade}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 10, lg: 2 }}>
                  <TextField
                    size="small"
                    select
                    label="From Year"
                    name="psch2fy"
                    required
                    disabled={pSchl2Status < 1}
                    fullWidth
                    defaultValue={student.previousSchools?.length > 1 ? student.previousSchools[1]?.fromYear : ""}
                  >
                    {getPreviousYears(15)?.map((year: number) => (
                      <MenuItem value={year} key={"psch2fy" + year}>
                        {year}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 10, lg: 2 }}>
                  <TextField
                    size="small"
                    select
                    label="To Year"
                    name="psch2ty"
                    required
                    disabled={pSchl2Status < 1}
                    fullWidth
                    defaultValue={student.previousSchools?.length > 1 ? student.previousSchools[1]?.toYear : ""}
                  >
                    {getPreviousYears(15)?.map((year: number) => (
                      <MenuItem value={year} key={"psch2ty" + year}>
                        {year}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            )}
          </>
        ) : (
          ""
        )}

        <Grid container spacing={2} sx={{ justifyContent: rowJustify, mb: 2 }}>
          <Grid size={{ xs: 12, lg: 12 }}>
            <Typography variant="subtitle1" sx={sectionHeadingSx}>
              Guardian Information
            </Typography>
          </Grid>
          <Grid size={{ xs: 10, lg: 12 }}>
            <TextField
              size="small"
              select
              label="Guardian"
              name="guardian"
              fullWidth
              value={guardian}
              onChange={(e) => setGuardian(e.target.value)}
            >
              {[GuardianType.FATHER, GuardianType.MOTHER, GuardianType.OTHER].map((mc: string) => (
                <MenuItem value={mc} key={"md" + mc}>
                  {mc}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        {guardian === GuardianType.OTHER ? (
          <Grid container spacing={2} sx={{ justifyContent: rowJustify, pt: 3, mb: 2 }}>
            <Grid size={{ xs: 10, lg: 2 }}>
              <MobileNumberField
                label="Mobile No"
                name="g-mobile"
                value={guardianMobile}
                onChange={(val) => {
                  setGuardianMobile(val);
                  lookupPersonByMobile(val, setGuardianFields, setGuardianLocked);
                }}
                required
                fullWidth />
            </Grid>
            <Grid size={{ xs: 10, lg: 2 }}>
              <TextField
                size="small"
                name="g-name"
                label="Guardian Name *"
                placeholder="Guardian Name *"
                type="text"
                fullWidth
                value={guardianFields.name}
                onChange={(e) => setGuardianFields({ ...guardianFields, name: e.target.value })}
                disabled={guardianLocked}
                InputProps={{ readOnly: guardianLocked }}
              />
            </Grid>
            <Grid size={{ xs: 10, lg: 2 }}>
              <DatePicker
                label="Date of Birth "
                format="DD-MMM-YYYY"
                value={guardianFields.dob}
                onChange={(newValue) => setGuardianFields({ ...guardianFields, dob: newValue })}
                maxDate={dayjs()}
                disabled={guardianLocked}
                readOnly={guardianLocked}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Grid>
            <Grid size={{ xs: 10, lg: 2 }}>
              <TextField
                size="small"
                name="g-qualification"
                inputProps={{ maxLength: 20 }}
                label="Qualification"
                placeholder="Qualification"
                value={guardianFields.qualification}
                onChange={(e) => setGuardianFields({ ...guardianFields, qualification: e.target.value })}
                disabled={guardianLocked}
                InputProps={{ readOnly: guardianLocked }}
                type="text"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 10, lg: 2 }}>
              <TextField
                size="small"
                name="g-occupation"
                inputProps={{ maxLength: 20 }}
                label="Occupation"
                placeholder="Occupation"
                type="text"
                fullWidth
                value={guardianFields.occupation}
                onChange={(e) => setGuardianFields({ ...guardianFields, occupation: e.target.value })}
                disabled={guardianLocked}
                InputProps={{ readOnly: guardianLocked }}
              />
            </Grid>
            <Grid size={{ xs: 10, lg: 2 }}>
              <TextField
                size="small"
                name="g-m-income"
                label="Monthly Income"
                placeholder="Monthly Income"
                type="number"
                fullWidth
                value={guardianFields.monthlyIncome}
                onChange={(e) => setGuardianFields({ ...guardianFields, monthlyIncome: e.target.value })}
                disabled={guardianLocked}
                InputProps={{ readOnly: guardianLocked }}
                inputProps={{ onWheel: (e: any) => (e.target as HTMLElement).blur() }}
              />
            </Grid>
            <Grid size={{ xs: 10, lg: 2 }}>
              <TextField
                size="small"
                name="g-email"
                label="Email"
                placeholder="Email"
                type="text"
                fullWidth
                value={guardianFields.email}
                onChange={(e) => setGuardianFields({ ...guardianFields, email: e.target.value })}
                disabled={guardianLocked}
                InputProps={{ readOnly: guardianLocked }}
              />
            </Grid>
            <Grid size={{ xs: 10, lg: 2 }}>
              <TextField
                size="small"
                select
                label="Relation *"
                name="g-relation"
                required
                InputLabelProps={{ required: false }}
                fullWidth
                value={student.guardianContactRelation || ""}
                onChange={(e) => setStudent({ ...student, guardianContactRelation: e.target.value })}
              >
                {getStudentRelationsExceptParents()?.map((relation: string) => (
                  <MenuItem value={relation} key={"grel" + relation}>
                    {relation}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        ) : (
          ""
        )}

        <Grid container spacing={2} sx={{ justifyContent: rowJustify, mb: 2 }}>
          <Grid size={{ xs: 12, lg: 12 }}>
            <Typography variant="subtitle1" sx={sectionHeadingSx}>
              Father Information
            </Typography>
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <MobileNumberField
              label="Mobile No"
              name="f-mobile"
              value={fatherMobile}
              onChange={(val) => {
                setFatherMobile(val);
                lookupPersonByMobile(val, setFatherFields, setFatherLocked);
              }}
              required={guardian === GuardianType.FATHER}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              name="f-name"
              label="Father Name *"
              placeholder="Father Name *"
              type="text"
              required
              InputLabelProps={{ required: false }}
              fullWidth
              value={fatherFields.name}
              onChange={(e) => setFatherFields({ ...fatherFields, name: e.target.value })}
              disabled={fatherLocked}
              InputProps={{ readOnly: fatherLocked }}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <DatePicker
              label="Date of Birth"
              format="DD-MMM-YYYY"
              value={fatherFields.dob}
              onChange={(newValue) => setFatherFields({ ...fatherFields, dob: newValue })}
              maxDate={dayjs()}
              disabled={fatherLocked}
              readOnly={fatherLocked}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              name="f-qualification"
              label="Qualification"
              placeholder="Qualification"
              inputProps={{ maxLength: 20 }}
              type="text"
              fullWidth
              value={fatherFields.qualification}
              onChange={(e) => setFatherFields({ ...fatherFields, qualification: e.target.value })}
              disabled={fatherLocked}
              InputProps={{ readOnly: fatherLocked }}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              name="f-occupation"
              label="Occupation"
              placeholder="Occupation"
              inputProps={{ maxLength: 20 }}
              type="text"
              fullWidth
              value={fatherFields.occupation}
              onChange={(e) => setFatherFields({ ...fatherFields, occupation: e.target.value })}
              disabled={fatherLocked}
              InputProps={{ readOnly: fatherLocked }}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              name="f-m-income"
              label="Monthly Income"
              placeholder="Monthly Income"
              type="number"
              fullWidth
              value={fatherFields.monthlyIncome}
              onChange={(e) => setFatherFields({ ...fatherFields, monthlyIncome: e.target.value })}
              disabled={fatherLocked}
              InputProps={{ readOnly: fatherLocked }}
              inputProps={{ onWheel: (e: any) => (e.target as HTMLElement).blur() }}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              name="f-email"
              label="Email"
              placeholder="Email"
              type="text"
              fullWidth
              value={fatherFields.email}
              onChange={(e) => setFatherFields({ ...fatherFields, email: e.target.value })}
              disabled={fatherLocked}
              InputProps={{ readOnly: fatherLocked }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ justifyContent: rowJustify, mb: 2 }}>
          <Grid size={{ xs: 12, lg: 12 }}>
            <Typography variant="subtitle1" sx={sectionHeadingSx}>
              Mother Information
            </Typography>
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <MobileNumberField
              label="Mobile No"
              name="m-mobile"
              value={motherMobile}
              onChange={(val) => {
                setMotherMobile(val);
                lookupPersonByMobile(val, setMotherFields, setMotherLocked);
              }}
              required={guardian === GuardianType.MOTHER}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              name="m-name"
              label="Mother Name *"
              placeholder="Mother Name *"
              type="text"
              required
              InputLabelProps={{ required: false }}
              fullWidth
              value={motherFields.name}
              onChange={(e) => setMotherFields({ ...motherFields, name: e.target.value })}
              disabled={motherLocked}
              InputProps={{ readOnly: motherLocked }}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <DatePicker
              label="Date of Birth "
              format="DD-MMM-YYYY"
              value={motherFields.dob}
              onChange={(newValue) => setMotherFields({ ...motherFields, dob: newValue })}
              maxDate={dayjs()}
              disabled={motherLocked}
              readOnly={motherLocked}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              name="m-qualification"
              inputProps={{ maxLength: 20 }}
              label="Qualification"
              placeholder="Qualification"
              type="text"
              fullWidth
              value={motherFields.qualification}
              onChange={(e) => setMotherFields({ ...motherFields, qualification: e.target.value })}
              disabled={motherLocked}
              InputProps={{ readOnly: motherLocked }}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              name="m-occupation"
              inputProps={{ maxLength: 20 }}
              label="Occupation"
              placeholder="Occupation"
              type="text"
              fullWidth
              value={motherFields.occupation}
              onChange={(e) => setMotherFields({ ...motherFields, occupation: e.target.value })}
              disabled={motherLocked}
              InputProps={{ readOnly: motherLocked }}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              name="m-m-income"
              label="Monthly Income"
              placeholder="Monthly Income"
              type="number"
              fullWidth
              value={motherFields.monthlyIncome}
              onChange={(e) => setMotherFields({ ...motherFields, monthlyIncome: e.target.value })}
              disabled={motherLocked}
              InputProps={{ readOnly: motherLocked }}
              inputProps={{ onWheel: (e: any) => (e.target as HTMLElement).blur() }}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 2 }}>
            <TextField
              size="small"
              name="m-email"
              label="Email"
              placeholder="Email"
              type="text"
              fullWidth
              value={motherFields.email}
              onChange={(e) => setMotherFields({ ...motherFields, email: e.target.value })}
              disabled={motherLocked}
              InputProps={{ readOnly: motherLocked }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ justifyContent: rowJustify, pt: 3, mb: 2 }}>
          <Grid size={{ xs: 12, lg: 12 }}>
            <Typography variant="subtitle1" sx={sectionHeadingSx}>
              Medical Information
            </Typography>
          </Grid>
          <Grid size={{ xs: 10, lg: 6 }}>
            <TextField
              size="small"
              select
              label="Select"
              name="std-medical-disability-select"
              fullWidth
              defaultValue="No"
              onChange={(e) => {
                if (e.target.value === "Yes") setIsStdSplMedicalAbled(true);
                else setIsStdSplMedicalAbled(false);
              }}
              inputRef={stdSplMedicalAbledRef}
              helperText="Any medical Condition/Allergy that school should be aware of ?"
            >
              {["No", "Yes"].map((mc: string) => (
                <MenuItem value={mc} key={"md" + mc}>
                  {mc}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {isStdSplMedicalAbled ? (
            <Grid size={{ xs: 10, lg: 6 }}>
              <TextField
                size="small"
                name="std-medical-disability-text"
                label="Please specify *"
                placeholder="Please specify"
                type="text"
                required
                InputLabelProps={{ required: false }}
                fullWidth
                defaultValue={
                  student.notes !== undefined && student.notes !== null && student.notes?.length > 0 ? student.notes[0]?.comment : undefined
                }
              />
            </Grid>
          ) : (
            ""
          )}
        </Grid>
        <Grid container spacing={2} sx={{ justifyContent: rowJustify, mb: 2 }}>
          <Grid size={{ xs: 10, lg: 6 }}>
            <TextField
              size="small"
              select
              label="Any Learning Disabilities"
              name="std-learn-disability-select"
              fullWidth
              defaultValue="No"
              onChange={(e) => {
                if (e.target.value === "Yes") setIsStdSplLearningAbled(true);
                else setIsStdSplLearningAbled(false);
              }}
              inputRef={stdSplLearningAbledRef}
            >
              {["No", "Yes"].map((mc: string) => (
                <MenuItem value={mc} key={"ld" + mc}>
                  {mc}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {isStdSplLearningAbled ? (
            <Grid size={{ xs: 10, lg: 6 }}>
              <TextField
                size="small"
                name="std-learn-disability-text"
                label="Please specify *"
                placeholder="Please specify *"
                type="text"
                required
                InputLabelProps={{ required: false }}
                fullWidth
                defaultValue={
                  student.notes !== undefined && student.notes !== null && student.notes?.length > 1 ? student.notes[1]?.comment : undefined
                }
              />
            </Grid>
          ) : (
            ""
          )}
        </Grid>
        <Grid container spacing={2} sx={{ justifyContent: rowJustify, mb: 2 }}>
          <Grid size={{ xs: 10, lg: 6 }}>
            <TextField
              size="small"
              name="r-medication-text"
              label="Regular Medication (Specify)"
              placeholder="Regular Medication (Specify)"
              type="text"
              fullWidth
              defaultValue={
                student.notes !== undefined && student.notes !== null && student.notes?.length > 2 ? student.notes[2]?.comment : undefined
              }
            />
          </Grid>
        </Grid>

        <Grid size={{ xs: 12, lg: 12 }}>
          <Typography variant="subtitle1" sx={sectionHeadingSx}>
            Emergency Contact *
          </Typography>
        </Grid>
        <Grid container spacing={2} sx={{ justifyContent: rowJustify, mb: 2 }}>
          <Grid size={{ xs: 10, lg: 3 }}>
            <MobileNumberField
              label="Mobile No"
              name="emg-phone"
              value={emergencyPhone}
              onChange={(val) => {
                setEmergencyPhone(val);
                lookupEmergencyPerson(val);
              }}
              required
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 3 }}>
            <TextField
              size="small"
              name="emg-name"
              sx={{ textTransform: "uppercase" }}
              label="Name *"
              placeholder="Name *"
              type="text"
              required
              InputLabelProps={{ required: false }}
              fullWidth
              value={emergencyName}
              onChange={(e) => setEmergencyName(e.target.value)}
              disabled={emergencyLocked}
              InputProps={{ readOnly: emergencyLocked }}
            />
          </Grid>
          <Grid size={{ xs: 10, lg: 3 }}>
            <TextField
              size="small"
              select
              label="Relation"
              name="emg-relation"
              required
              fullWidth
              value={student.emergencyContactRelation || ""}
              onChange={(e) => setStudent({ ...student, emergencyContactRelation: e.target.value })}
            >
              {getStudentRelations()?.map((relation: string) => (
                <MenuItem value={relation} key={"emg-r" + relation}>
                  {relation}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ justifyContent: "center", pt: 2, pb: 3, borderTop: "1px solid #e5e7eb" }}>
          <Grid size={{ xs: 10, lg: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="small"
              fullWidth
              sx={{ py: 1.1, fontWeight: 600, borderRadius: 2, textTransform: "none", fontSize: 15, boxShadow: "0 8px 24px rgba(25, 118, 210, 0.25)" }}
              loading={isAdmissionFormSubmitting}
              loadingIndicator="Submitting…"
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};
